import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { sendContact } from "../api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Mail, Phone, MapPin, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppLink } from "../mock";

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contactFill"));
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success(t("contactSent"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t("contactError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="max-w-7xl mx-auto px-6 py-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
        <div className="bg-[#0d3b43] rounded-3xl p-8 sm:p-10 text-white flex flex-col justify-center">
          <span className="text-cyan-300 font-bold text-sm uppercase tracking-widest">
            {t("navContact")}
          </span>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl mt-2">
            {t("contactTitle")}
          </h2>
          <p className="text-white/75 mt-3 max-w-md">{t("contactSub")}</p>

          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center text-cyan-300">
                <Phone className="w-5 h-5" />
              </span>
              <span className="font-semibold">+52 998 790 7699</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center text-cyan-300">
                <Mail className="w-5 h-5" />
              </span>
              <span className="font-semibold">contact@rsvcancun.com</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center text-cyan-300">
                <MapPin className="w-5 h-5" />
              </span>
              <span className="font-semibold">Cancún, Quintana Roo, México</span>
            </div>
          </div>

          <a
            href={buildWhatsAppLink(t("whatsappMsg"))}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold rounded-full px-6 py-3.5 transition-colors w-full sm:w-auto"
          >
            <MessageCircle className="w-5 h-5" />
            {t("whatsappCta")}
          </a>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-teal-100 rounded-3xl p-8 sm:p-10 rc-shadow"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="c-name">{t("yourName")}</Label>
              <Input
                id="c-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("fullNamePh")}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-email">{t("yourEmail")}</Label>
                <Input
                  id="c-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <Label htmlFor="c-phone">{t("yourPhone")}</Label>
                <Input
                  id="c-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 555 000 0000"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="c-msg">{t("yourMessage")}</Label>
              <Textarea
                id="c-msg"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="..."
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-extrabold rounded-full h-11"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {t("sendMessage")}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
